/**
 * Button Component
 *
 * A versatile button component with multiple variants and sizes
 */

import React from 'react';
import { cn } from '@/base/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', asChild: _asChild = false, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

    const variants = {
      default: 'bg-[var(--ems-primary)] text-white hover:bg-[var(--ems-primary-hover)] focus-visible:ring-[var(--ems-primary)]',
      secondary: 'bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)] hover:bg-[var(--grok-surface-3)] focus-visible:ring-[var(--grok-border)]',
      destructive: 'bg-[var(--ems-danger)] text-white hover:bg-[var(--ems-danger)] focus-visible:ring-[var(--ems-danger)]',
      outline: 'border border-[var(--grok-border)] bg-transparent hover:bg-[var(--grok-surface-2)] text-[var(--grok-text-secondary)] focus-visible:ring-[var(--grok-border)]',
      ghost: 'hover:bg-[var(--grok-surface-2)] hover:text-[var(--grok-text-primary)] focus-visible:ring-[var(--grok-border)]',
      link: 'text-[var(--ems-primary)] underline-offset-4 hover:underline',
    };

    const sizes = {
      default: 'h-10 px-4 py-2',
      sm: 'h-8 px-3 text-xs',
      lg: 'h-11 px-8',
      icon: 'h-10 w-10',
    };

    return (
      <button
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button };
